import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Gift, Calendar, Sparkles, CheckCircle2 } from 'lucide-react';
import { authAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useVoucher } from '../contexts/VoucherContext';
import './DailyLoginModal.css';

const DailyLoginModal = ({ isOpen, onClose, dailyLoginData, onVoucherAdded, onShowToast, onNavigate, onRefreshDailyLogin }) => {
  const [claiming, setClaiming] = useState(false);
  const [localDailyLoginData, setLocalDailyLoginData] = useState(dailyLoginData);
  const { user } = useAuth();
  
  // Get refreshVouchersFromDatabase - use optional chaining to safely access
  // This will be null if VoucherProvider isn't available, which is fine
  // The voucher is still saved to localStorage and database
  let refreshVouchersFromDatabase = null;
  try {
    // Always call the hook (Rules of Hooks), but handle the error if context isn't available
    const voucherContext = useVoucher();
    refreshVouchersFromDatabase = voucherContext?.refreshVouchersFromDatabase || null;
  } catch (error) {
    // VoucherProvider not available - this can happen if DailyLoginModal is rendered
    // before VoucherProvider is mounted. We'll just skip the refresh in this case.
    // The voucher is still saved to localStorage and database, so it will appear on next page load.
    refreshVouchersFromDatabase = null;
  }

  // Update local data when prop changes
  useEffect(() => {
    setLocalDailyLoginData(dailyLoginData);
  }, [dailyLoginData]);
  
  // Show toast via custom event (ToastContainer will listen for this)
  const showToast = (message, type = 'info') => {
    // Dispatch custom event that ToastContainer can listen to
    window.dispatchEvent(new CustomEvent('show-toast', { 
      detail: { message, type } 
    }));
    
    // Also call callback if provided
    if (onShowToast) {
      onShowToast(message, type);
    }
  };
  
  // Add voucher directly to localStorage and notify voucher context
  const addVoucherToStorage = (voucher) => {
    try {
      const existingVouchers = JSON.parse(localStorage.getItem('vouchers') || '[]');
      const currentUserId = user?._id || user?.id;
      // Generate a unique ID if not present
      const voucherId = voucher.id || Date.now() + Math.random();
      
      // If this is a 7-day login reward, override the description per request
      const friendlyDescription = (voucher.isDailyLoginReward || voucher.description?.includes('7-Day Daily Login Reward'))
        ? '7 day login reward! thank you for being loyal with us!'
        : voucher.description;

      const newVoucher = {
        ...voucher,
        id: voucherId, // Ensure id is always present
        usedCount: voucher.usedCount !== undefined ? voucher.usedCount : 0,
        createdAt: voucher.createdAt || new Date().toISOString(),
        isActive: voucher.isActive !== undefined ? voucher.isActive : true,
        // Preserve userId and isDailyLoginReward from backend if present, otherwise use defaults
        userId: voucher.userId ? String(voucher.userId) : (currentUserId ? String(currentUserId) : null),
        isDailyLoginReward: voucher.isDailyLoginReward !== undefined ? voucher.isDailyLoginReward : true,
        description: friendlyDescription || '7 day login reward! thank you for being loyal with us!'
      };
      
      // Ensure userId is set (should come from backend, but fallback to current user)
      if (!newVoucher.userId && currentUserId) {
        newVoucher.userId = String(currentUserId);
      }
      
      // Ensure all required fields are present
      if (!newVoucher.code) {
        console.error('❌ Voucher missing code field!', voucher);
        return;
      }
      
      
      existingVouchers.push(newVoucher);
      localStorage.setItem('vouchers', JSON.stringify(existingVouchers));
      
      
      // Notify parent component if callback provided
      if (onVoucherAdded) {
        onVoucherAdded(newVoucher);
      }

      // Notify VoucherContext in the same tab via a custom event
      window.dispatchEvent(new CustomEvent('vouchers-updated', { 
        detail: { vouchers: existingVouchers }
      }));
      
    } catch (error) {
      console.error('❌ Error adding voucher to storage:', error);
    }
  };

  // Use local data (which may be updated after claiming) or fallback to prop
  const displayData = localDailyLoginData || dailyLoginData;
  const { isNewLogin, consecutiveDays, totalDays, canClaimReward } = displayData || {};

  const handleClaimReward = async () => {
    if (claiming) return;

    setClaiming(true);
    
    // Close modal immediately - don't wait for API response
    onClose();

    try {
      const response = await authAPI.claimDailyLoginReward();
      
      // API interceptor unwraps response.data, so response is already the data object
      if (response.success) {
        const voucher = response.data.voucher;
        const updatedConsecutiveDays = response.data.consecutiveDays; // Get updated streak from backend
        
        if (!voucher) {
          console.error('❌ No voucher in response:', response);
          showToast('Failed to claim reward: No voucher received', 'error');
          return;
        }
        
        
        // Add voucher to localStorage (VoucherContext will pick it up on next render)
        addVoucherToStorage(voucher);
        
        // Refresh vouchers from database to ensure the new voucher appears
        // Use a small delay to ensure the database save is complete
        if (refreshVouchersFromDatabase) {
          setTimeout(async () => {
            try {
              await refreshVouchersFromDatabase(false, true); // Silent refresh
            } catch (refreshError) {
              console.error('Error refreshing vouchers:', refreshError);
              // Don't show error to user, voucher is already saved
            }
          }, 1000);
        }
        
        // Show toast with the specific voucher code
        showToast(`🎉 Reward claimed! Voucher code ${voucher.code} has been added to your account!`, 'success');
        
        // Refresh daily login status in AuthContext
        if (onRefreshDailyLogin) {
          onRefreshDailyLogin();
        }
        
        // Navigate to profile page with vouchers section after a short delay
        setTimeout(() => {
          if (onNavigate) {
            // Use callback if provided (from parent component with Router context)
            onNavigate('/profile?tab=profile&scrollTo=vouchers');
          } else {
            // Fallback to window.location if no callback provided
            window.location.href = '/profile?tab=profile&scrollTo=vouchers';
          }
        }, 500);
      } else {
        console.error('❌ Claim reward failed:', response);
        showToast(response.message || 'Failed to claim reward', 'error');
      }
    } catch (error) {
      console.error('❌ Claim reward error:', error);
      const errorMessage = error.message || error.response?.data?.message || 'Failed to claim reward';
      showToast(errorMessage, 'error');
      
      // If already claimed, refresh the daily login data to update the UI
      if (errorMessage.includes('already claimed')) {
        if (onRefreshDailyLogin) {
          onRefreshDailyLogin();
        }
      }
    } finally {
      setClaiming(false);
    }
  };

  if (!isOpen) return null;

  const days = Array.from({ length: 7 }, (_, i) => i + 1);
  const progress = Math.min(consecutiveDays || 0, 7);

  return createPortal(
    <div className="daily-login-overlay" onClick={onClose}>
      <div className="daily-login-modal" onClick={(e) => e.stopPropagation()}>
        <button
          className="daily-login-close"
          onClick={onClose}
          aria-label="Close"
          type="button"
        >
          <X size={20} />
        </button>

        <div className="daily-login-content">
          {/* Header */}
          <div className="daily-login-header">
            <div className="daily-login-icon-wrapper">
              <Calendar size={32} className="daily-login-icon" />
            </div>
            <h2 className="daily-login-title">Daily Login</h2>
            <p className="daily-login-subtitle">
              {isNewLogin 
                ? `Day ${consecutiveDays} of your streak! 🌱`
                : `You're on day ${consecutiveDays} of your streak! 🌿`
              }
            </p>
          </div>

          {/* Progress Bar */}
          <div className="daily-login-progress-section">
            <div className="daily-login-days-grid">
              {days.map((day) => {
                const isCompleted = day <= progress;
                const isToday = day === progress && isNewLogin;
                const isRewardDay = day === 7;

                const handleDayClick = () => {
                  // Only allow clicking if it's the reward day and can be claimed
                  if (isRewardDay && !claiming && canClaimReward) {
                    handleClaimReward();
                  }
                  // If not claimable, do nothing (no toast, no action)
                };

                // Determine if day 7 should be clickable
                const isClickable = isRewardDay && canClaimReward && !claiming;

                return (
                  <div
                    key={day}
                    className={`daily-login-day ${isCompleted ? 'completed' : ''} ${isToday ? 'today' : ''} ${isRewardDay ? 'reward-day' : ''} ${isRewardDay && !canClaimReward ? 'already-claimed' : ''}`}
                    onClick={isClickable ? handleDayClick : undefined}
                    role={isRewardDay ? 'button' : undefined}
                    tabIndex={isClickable ? 0 : undefined}
                    aria-label={isRewardDay ? (canClaimReward ? 'Claim 7-day reward' : 'Reward not available yet') : `Day ${day} of streak`}
                    style={isRewardDay && !canClaimReward ? { cursor: 'not-allowed', opacity: 0.6, pointerEvents: 'none' } : {}}
                  >
                    {isCompleted ? (
                      <CheckCircle2 size={24} className="day-check-icon" />
                    ) : (
                      <span className="day-number">{day}</span>
                    )}
                    {isRewardDay && (
                      <div className="reward-badge">
                        <Gift size={16} />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Progress Bar */}
            <div className="daily-login-progress-bar">
              <div
                className="daily-login-progress-fill"
                style={{ width: `${(progress / 7) * 100}%` }}
              />
            </div>

            <p className="daily-login-progress-text">
              {progress < 7
                ? `${7 - progress} more day${7 - progress > 1 ? 's' : ''} to unlock your reward!`
                : '🎉 You\'ve completed 7 days! Claim your reward!'
              }
            </p>
          </div>

          {/* Reward Info */}
          {canClaimReward && (
            <div className="daily-login-reward-section">
              <div className="reward-card">
                <div className="reward-icon-wrapper">
                  <Gift size={40} className="reward-icon" />
                  <Sparkles size={20} className="reward-sparkle sparkle-1" />
                  <Sparkles size={16} className="reward-sparkle sparkle-2" />
                  <Sparkles size={18} className="reward-sparkle sparkle-3" />
                </div>
                <h3 className="reward-title">7-Day Reward Unlocked!</h3>
                <p className="reward-description">
                  Claim your <strong>10% off voucher</strong> with maximum discount of <strong>₱200</strong>!
                </p>
                <p className="reward-details">
                  Valid for 30 days • One-time use
                </p>
                <button
                  className="reward-claim-btn"
                  onClick={handleClaimReward}
                  disabled={claiming}
                >
                  {claiming ? 'Claiming...' : 'Claim Reward'}
                </button>
              </div>
            </div>
          )}

          {/* Stats */}
          <div className="daily-login-stats">
            <div className="stat-item">
              <span className="stat-label">Current Streak</span>
              <span className="stat-value">{consecutiveDays || 0} days</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Total Logins</span>
              <span className="stat-value">{totalDays || 0} days</span>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default DailyLoginModal;

